// Tax Report Hook (G50 - Algerian TVA Declaration)
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import type { DateRange, TaxReport, G50Data } from '@/types/report';
import { calculateG50 } from '../utils/calculations';

export function useTaxReport(period: DateRange) {
  const { company } = useAuthStore();

  return useQuery({
    queryKey: ['tax-report', company?.id, period.start.toISOString(), period.end.toISOString()],
    queryFn: async (): Promise<TaxReport> => {
      if (!company?.id) throw new Error('No company selected');

      // Fetch sales (invoices) for TVA collectée
      const { data: invoices, error: invoicesError } = await supabase
        .from('invoices')
        .select('total_ht, total_tva, created_at')
        .eq('company_id', company.id)
        .gte('created_at', period.start.toISOString())
        .lte('created_at', period.end.toISOString());

      if (invoicesError) throw invoicesError;

      // Fetch purchases (expense transactions) for TVA déductible
      const { data: expenses, error: expensesError } = await supabase
        .from('treasury_transactions')
        .select('amount, date')
        .eq('company_id', company.id)
        .eq('type', 'expense')
        .gte('date', period.start.toISOString().split('T')[0])
        .lte('date', period.end.toISOString().split('T')[0]);

      if (expensesError) throw expensesError;

      // Calculate totals
      const salesHT = invoices?.reduce((sum, inv) => sum + (inv.total_ht || 0), 0) || 0;
      const tvaCollectee = invoices?.reduce((sum, inv) => sum + (inv.total_tva || 0), 0) || 0;

      // For expenses, assume included TVA (divide by 1.19 to get HT)
      const purchasesTTC = expenses?.reduce((sum, exp) => sum + (exp.amount || 0), 0) || 0;
      const purchasesHT = Math.round((purchasesTTC / 1.19) * 100) / 100;
      const tvaDeductible = purchasesTTC - purchasesHT;

      // Get current month/year from period
      const periodDate = new Date(period.start);
      const month = periodDate.getMonth() + 1;
      const year = periodDate.getFullYear();

      // Calculate G50 for the period
      const g50: G50Data = calculateG50(
        salesHT,
        purchasesHT,
        {
          name: company.name || '',
          nif: company.nif || '',
          address: company.address || '',
          wilaya: company.wilaya || '',
        },
        month,
        year
      );

      // Calculate monthly breakdown for the period
      const monthlyMap = new Map<string, {
        salesHT: number;
        tvaCollectee: number;
        purchasesHT: number;
        tvaDeductible: number;
      }>();

      // Group invoices by month
      invoices?.forEach(inv => {
        const date = new Date(inv.created_at);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        const existing = monthlyMap.get(monthKey);
        if (existing) {
          existing.salesHT += inv.total_ht || 0;
          existing.tvaCollectee += inv.total_tva || 0;
        } else {
          monthlyMap.set(monthKey, {
            salesHT: inv.total_ht || 0,
            tvaCollectee: inv.total_tva || 0,
            purchasesHT: 0,
            tvaDeductible: 0,
          });
        }
      });

      // Group expenses by month
      expenses?.forEach(exp => {
        const monthKey = exp.date.substring(0, 7); // YYYY-MM
        const expHT = Math.round(((exp.amount || 0) / 1.19) * 100) / 100;
        const expTVA = (exp.amount || 0) - expHT;
        
        const existing = monthlyMap.get(monthKey);
        if (existing) {
          existing.purchasesHT += expHT;
          existing.tvaDeductible += expTVA;
        } else {
          monthlyMap.set(monthKey, {
            salesHT: 0,
            tvaCollectee: 0,
            purchasesHT: expHT,
            tvaDeductible: expTVA,
          });
        }
      });

      const monthlyBreakdown = Array.from(monthlyMap.entries())
        .map(([month, data]) => ({
          month,
          salesHT: Math.round(data.salesHT * 100) / 100,
          tvaCollectee: Math.round(data.tvaCollectee * 100) / 100,
          purchasesHT: Math.round(data.purchasesHT * 100) / 100,
          tvaDeductible: Math.round(data.tvaDeductible * 100) / 100,
          netTVA: Math.round((data.tvaCollectee - data.tvaDeductible) * 100) / 100,
        }))
        .sort((a, b) => a.month.localeCompare(b.month));

      return {
        period,
        g50,
        monthlyBreakdown,
      };
    },
    enabled: !!company?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
